import { creatorbase } from '@round/api';
import { GenericDropdownOption } from 'App.types';
import useSelectProps, { SelectProps } from 'Hooks/useSelectProps';
import { useUsers, useUsersListInfinite } from '@round/creatorbase-helpers/api-hooks';
import { useMemo, useRef } from 'react';

export type UserOption = GenericDropdownOption<number> & {
    is_creatorbase_admin: boolean;
};

export const mapUserToOption = (user: creatorbase.User): UserOption => ({
    value: user.id,
    label: user.name,
    is_creatorbase_admin: user.is_creatorbase_admin,
});

type Params = {
    initOn: 'menuOpen' | 'mount';
    initialValueIds?: number[];
    params?: Pick<creatorbase.GetUsersParams, 'team_id' | 'client_id' | 'is_creatorbase_admin' | 'id'>;
    enabled?: boolean;
};

type Return = {
    getProps: (override?: Pick<SelectProps<UserOption, true>, 'onChange'>) => SelectProps<UserOption, true>;
};

export function useUsersMultiSelect({ enabled = true, ...params }: Params = { initOn: 'mount' }): Return {
    // Freeze initial IDs at mount so user-driven selection changes don't echo back into a new initial-value fetch.
    const initialIdsRef = useRef(params.initialValueIds);
    const initialIds = initialIdsRef.current ?? [];
    const hasInitialIds = initialIds.length > 0;
    const { data: initialUsersData } = useUsers(
        {
            id: initialIds.join(','),
            page_size: initialIds.length,
        },
        { enabled: enabled && hasInitialIds }
    );
    const initialValue = useMemo(() => initialUsersData?.results.map(mapUserToOption) ?? null, [initialUsersData]);

    const { getProps, isMenuOpen, debouncedSearchValue } = useSelectProps<UserOption, true>({ initialValue });

    const { data, isFetching, hasNextPage, fetchNextPage, isError } = useUsersListInfinite(
        { ...params.params, page_size: 25, search: debouncedSearchValue },
        { enabled: enabled && ((params.initOn === 'menuOpen' && isMenuOpen) || params.initOn === 'mount') }
    );

    const options =
        data?.pages.flatMap((page) => {
            return page.results.map(mapUserToOption);
        }) ?? [];

    return {
        getProps: (override) =>
            getProps({
                ...override,
                isMulti: true,
                options,
                noOptionsMessage: () => (isError ? 'Could not get users' : 'No users found'),
                isLoading: isFetching,
                onMenuScrollToBottom: () => {
                    if (hasNextPage && !isFetching) {
                        fetchNextPage();
                    }
                },
                filterOption: null,
            }),
    };
}
